import { useState, type ReactNode } from 'react';
import { passthrough } from '../primitives/passthrough.js';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';

/**
 * KanbanBoard - stage columns with totals, and drag that can be refused.
 *
 * Three things it insists on.
 *
 * **A column header carries a count and a total, in tabular figures.** Those
 * numbers come from the server's count() and sum(); this component never adds
 * up the cards it was given, because the cards are a page and the total is not.
 *
 * **A move is optimistic and reversible.** The card moves immediately, and if
 * the server refuses it goes back - with the server's own sentence, not a
 * generic apology. A board that waits for the server before moving feels
 * broken; a board that moves and silently keeps a move the server rejected is
 * broken.
 *
 * **Dragging is not the only way.** Every card has a keyboard path to every
 * column, because a board that can only be operated by mouse is a board half
 * the people cannot use.
 */

export interface BoardCard {
  id: string;
  href?: string | undefined;
  /** Rendered by the caller: a deal card carries four facts without opening. */
  content: ReactNode;
}

export interface BoardColumn {
  id: string;
  label: string;
  /** From count(). Already formatted. */
  count: string;
  /**
   * One already-formatted total per currency present.
   *
   * An array rather than a string, because a column holding two currencies has
   * no single total until there is an exchange rate. Rendering one anyway
   * would be a wrong number that looks exactly like a right one.
   */
  totals: string[];
  outcome?: 'open' | 'won' | 'lost';
  cards: BoardCard[];
}

export interface KanbanLabels {
  /** The accessible name of the move control on a card. */
  moveTo: (card: string, column: string) => string;
  moveCard: string;
  /** Shown while a move is in flight, and when one is put back. */
  moving: string;
  reverted: (reason: string) => string;
  /**
   * What an empty column teaches.
   *
   * A function of the stage, because "Nothing here" says only that a person
   * can see. What they need is what puts a deal in THIS column - and a stage
   * whose entry condition nobody can state is a stage nobody uses correctly.
   */
  emptyColumn: (column: string) => string;
  /** Shown where a total would be, when a column holds nothing. */
  noTotal: string;
}

export interface KanbanBoardProps {
  columns: BoardColumn[];
  labels: KanbanLabels;
  /**
   * Resolves when the move stuck, rejects with the reason it did not.
   *
   * The component puts the card back on rejection and shows the reason, so a
   * caller cannot forget to.
   */
  onMove: (cardId: string, toColumnId: string) => Promise<void>;
  /** A card's name, for the accessible label on its move control. */
  cardLabel: (cardId: string) => string;
}

export function KanbanBoard({ columns, labels, onMove, cardLabel }: KanbanBoardProps): JSX.Element {
  // Where a card is right now as far as this component is concerned, which is
  // ahead of the server while a move is in flight.
  const [optimistic, setOptimistic] = useState<Record<string, string>>({});
  const [reverted, setReverted] = useState<{ cardId: string; reason: string } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  const columnOf = (card: BoardCard, declared: string): string => optimistic[card.id] ?? declared;

  async function move(cardId: string, from: string, to: string): Promise<void> {
    if (from === to) return;
    setReverted(null);
    setOptimistic((current) => ({ ...current, [cardId]: to }));
    try {
      await onMove(cardId, to);
      setOptimistic((current) => {
        const next = { ...current };
        delete next[cardId];
        return next;
      });
    } catch (error) {
      // Back where it was, with the server's own sentence. A board that keeps
      // a move the server rejected is lying about the pipeline.
      setOptimistic((current) => {
        const next = { ...current };
        delete next[cardId];
        return next;
      });
      setReverted({
        cardId,
        reason: error instanceof Error ? error.message : labels.moving,
      });
    }
  }

  return (
    <div data-component="KanbanBoard" className="flex w-full flex-col gap-2">
      {reverted ? (
        <p role="alert" data-move-reverted className="flex items-center gap-2 rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text">
          <Icon name="ArrowUUpLeft" size="inline" decorative />
          {labels.reverted(reverted.reason)}
        </p>
      ) : null}

      {/*
        * The board scrolls sideways; the page does not.
        *
        * Snapping is proximity rather than mandatory: a person dragging a card
        * across four stages must not have the board pulled out from under
        * them at every column edge.
        */}
      <div className="w-full snap-x snap-proximity overflow-x-auto pb-2" data-scroll-container>
        <div className="flex min-w-full items-start gap-4">
          {columns.map((column) => {
            const cards = columns
              .flatMap((c) => c.cards.map((card) => ({ card, declared: c.id })))
              .filter(({ card, declared }) => columnOf(card, declared) === column.id)
              .map(({ card }) => card);

            return (
              <section
                key={column.id}
                data-board-column={column.id}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const cardId = dragging ?? event.dataTransfer.getData('text/plain');
                  const from = columns.find((c) => c.cards.some((card) => card.id === cardId));
                  if (cardId && from) void move(cardId, optimistic[cardId] ?? from.id, column.id);
                  setDragging(null);
                }}
                /*
                 * 292px, so five stages are on screen before anybody scrolls.
                 * A board whose second half is always over the horizon gets
                 * used as a list of the first two columns.
                 */
                className="flex w-board-column shrink-0 snap-start flex-col gap-2 rounded-card bg-sunken p-2"
              >
                {/* Sticky, so the count and total stay visible while a long
                    column is scrolled. */}
                <header
                  className={cx(
                    'sticky top-0 z-10 flex flex-col gap-0.5 rounded-control bg-sunken px-2 py-1',
                    column.outcome === 'won' && 'text-success-text',
                    column.outcome === 'lost' && 'text-danger-text',
                  )}
                >
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-micro uppercase tracking-label text-ink-label">
                      {column.label}
                    </span>
                    <Num className="text-meta text-ink-meta">{column.count}</Num>
                  </span>
                  {/* Every currency present, one under the other. A column
                      with two of them says so rather than adding them up. */}
                  {column.totals.length === 0 ? (
                    <Num align="start" className="text-body text-ink-300">
                      {labels.noTotal}
                    </Num>
                  ) : (
                    column.totals.map((total) => (
                      <Num key={total} align="start" className="text-body text-ink">
                        {total}
                      </Num>
                    ))
                  )}
                </header>

                <ul className="flex flex-col gap-2">
                  {cards.length === 0 ? (
                    <li className="px-2 py-5 text-center text-dense text-ink-meta">
                      {labels.emptyColumn(column.label)}
                    </li>
                  ) : (
                    cards.map((card) => (
                      <li
                        key={card.id}
                        data-board-card={card.id}
                        draggable
                        onDragStart={(event) => {
                          setDragging(card.id);
                          event.dataTransfer.setData('text/plain', card.id);
                        }}
                        onDragEnd={() => setDragging(null)}
                        className={cx(
                          'rounded-card border border-rule bg-surface p-2',
                          dragging === card.id && 'opacity-60',
                        )}
                      >
                        {card.href ? (
                          <a href={card.href} className="block">
                            {card.content}
                          </a>
                        ) : (
                          card.content
                        )}

                        {/*
                          * The keyboard path, hidden until it is reached.
                          *
                          * This select was drawn on every card, repeating every
                          * stage name - most of the text on the board was the
                          * board telling itself what its own columns are
                          * called. It is not deleted, because a board operable
                          * only by mouse is a board half the people cannot
                          * use: it is visually hidden and comes back the
                          * moment focus lands inside it.
                          */}
                        <label className="sr-only mt-1 flex items-center gap-1 text-meta text-ink-meta focus-within:not-sr-only">
                          <span className="sr-only">{labels.moveCard}</span>
                          <select
                            data-move-card={card.id}
                            aria-label={labels.moveTo(cardLabel(card.id), column.label)}
                            value={column.id}
                            onChange={(event) => void move(card.id, column.id, event.target.value)}
                            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-1 text-meta text-ink"
                          >
                            {columns.map((target) => (
                              <option key={target.id} value={target.id}>
                                {target.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </li>
                    ))
                  )}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * HealthChip - a score with the reasons behind it.
 *
 * Colour PLUS word, always. A chip that says "at risk" only by being amber
 * says nothing to a reader who cannot see amber, and says nothing at all in a
 * printed pipeline review.
 *
 * Clicking opens the contributing factors. A health score nobody can
 * interrogate is a health score nobody will act on.
 */
export interface HealthChipLabels {
  /** The accessible name of the disclosure. */
  whyThisScore: string;
  factors: string;
}

export function HealthChip({
  level,
  label,
  factors,
  labels,
  ...rest
}: {
  level: 'healthy' | 'watch' | 'at-risk';
  label: string;
  factors: Array<{ label: string; effect: 'up' | 'down' }>;
  labels: HealthChipLabels;
} & Record<string, unknown>): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <span
      {...passthrough(rest)}
      data-component="HealthChip"
      className="relative inline-flex flex-col"
    >
      <button
        type="button"
        data-health={level}
        aria-expanded={open}
        aria-label={labels.whyThisScore}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          'inline-flex h-chip items-center gap-1 rounded-chip px-2 text-meta',
          'focus-visible:outline-none',
          level === 'healthy' && 'bg-success-tint text-success-text',
          level === 'watch' && 'bg-warning-tint text-warning-text',
          level === 'at-risk' && 'bg-danger-tint text-danger-text',
        )}
      >
        <Icon
          name={level === 'healthy' ? 'CheckCircle' : level === 'watch' ? 'WarningCircle' : 'Warning'}
          size="inline"
          decorative
        />
        {/* The word. Never colour alone. */}
        {label}
      </button>

      {open ? (
        <ul
          data-health-factors
          className="absolute top-full z-20 mt-1 flex w-panel-wide flex-col gap-1 rounded-card bg-surface p-2 shadow-popover"
        >
          <li className="text-micro uppercase tracking-label text-ink-label">{labels.factors}</li>
          {factors.map((factor) => (
            <li key={factor.label} className="flex items-center gap-2 text-dense text-ink">
              <Icon
                name={factor.effect === 'up' ? 'TrendUp' : 'TrendDown'}
                size="inline"
                decorative
              />
              {factor.label}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
